// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Modal + toast infrastructure and the static modal content (How it works,
 * Privacy / threat model, About). The modal host is hidden with the `hidden`
 * attribute and the global `[hidden]{display:none!important}` guard, so no
 * invisible overlay can ever swallow taps.
 */
import { icon } from './icons.ts';

let hostEl: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;

export function initModals(): void {
  const host = document.createElement('div');
  host.id = 'modal-host';
  host.hidden = true;
  host.innerHTML = `<div class="modal-backdrop" data-close></div><div class="modal" role="dialog" aria-modal="true"></div>`;
  document.body.appendChild(host);
  hostEl = host;
  host.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close !== undefined || t.closest('[data-close]')) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hostEl && !hostEl.hidden) closeModal();
  });
}

export function openModal(title: string, bodyHtml: string): void {
  if (!hostEl) return;
  lastFocus = document.activeElement as HTMLElement;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = `
    <div class="modal-head">
      <h2 class="modal-title">${escapeHtml(title)}</h2>
      <button type="button" class="icon-btn" data-close aria-label="Close">${icon('x')}</button>
    </div>
    <div class="modal-body">${bodyHtml}</div>`;
  hostEl.hidden = false;
  const close = modal.querySelector('[data-close]') as HTMLElement | null;
  close?.focus();
}

export function closeModal(): void {
  if (!hostEl) return;
  hostEl.hidden = true;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = '';
  lastFocus?.focus?.();
}

export function isModalOpen(): boolean {
  return !!hostEl && !hostEl.hidden;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(msg: string, kind: 'info' | 'ok' | 'err' = 'info'): void {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.dataset.kind = kind;
  el.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el?.classList.remove('show'), 2600);
}

function g(term: string, label?: string): string {
  return `<span class="glossary-link" data-term="${escapeHtml(term)}" tabindex="0" role="button">${escapeHtml(label ?? term)}</span>`;
}

export function howItWorksHtml(): string {
  return `
    <ol class="steps">
      <li><span class="step-n">1</span><div><strong>Open the link on both devices.</strong>
        The first device generates a random room id and a four-word ${g('passphrase', 'passphrase')}. Both live only in the link’s <code>#hash</code>, which browsers never send to a server. Open the same link on your other device (scan the QR).</div></li>
      <li><span class="step-n">2</span><div><strong>Pick a folder on each device.</strong>
        With the ${g('file system access', 'File System Access API')}, your browser asks permission to read and write one folder you choose. Nothing outside that folder is ever touched.</div></li>
      <li><span class="step-n">3</span><div><strong>The devices find each other.</strong>
        Each browser finds the other through a public ${g('signalling relay', 'signalling relay')} — used once, for the handshake only — then opens a direct ${g('data channel', 'WebRTC data channel')} with ${g('stun', 'STUN')}. No server sits in the file path.</div></li>
      <li><span class="step-n">4</span><div><strong>Confirm the four words.</strong>
        Both devices show the same ${g('sas', 'four-word code')}. If they match, no impostor slipped into the middle.</div></li>
      <li><span class="step-n">5</span><div><strong>Only the differences move.</strong>
        The devices swap a ${g('manifest', 'manifest')} — paths, sizes and times, never contents — and pull just the files that are new or changed. Each file is sealed with ${g('aes-gcm', 'AES-GCM-256')} before it leaves and opened only on the far side.</div></li>
      <li><span class="step-n">6</span><div><strong>Edits keep flowing.</strong>
        Syncwell watches each folder and propagates changes live using the ${g('version clock', 'file’s modified time')} as the version. It only ever adds or updates — <strong>it never deletes your files</strong>.</div></li>
    </ol>
    <p class="muted small">Folder sync needs a Chromium browser (Chrome, Edge, Brave, Arc). On other browsers, Syncwell falls back to picking files to send and receiving them as downloads.</p>`;
}

export function privacyHtml(): string {
  return `
    <div class="tm">
      <section><h3 class="tm-good">Protected</h3><ul>
        <li>Your <strong>file contents</strong> travel over a ${g('data channel', 'direct data channel')} between the two browsers, ${g('dtls', 'DTLS')}-encrypted, and are additionally sealed in ${g('aes-gcm', 'AES-GCM-256')} with a key derived from your ${g('passphrase', 'passphrase')}. No server is ever in the file path — the plaintext exists only inside your two tabs.</li>
        <li>The <strong>${g('passphrase', 'passphrase')}</strong> encrypts the ${g('signalling relay', 'signalling')} handshake and derives both the file key and the ${g('sas', 'four verification words')}. It lives only in the link’s hash and never reaches any server.</li>
        <li>File <strong>names, sizes and times</strong> live in the encrypted ${g('manifest', 'manifest')} on the data channel, so a relay never sees them.</li>
        <li><strong>Syncwell never deletes.</strong> A peer can only add or update files in your folder — removing a file on one device never removes it on the other. No account, no cookies, no third-party fonts, no history.</li>
      </ul></section>
      <section><h3 class="tm-bad">Not protected</h3><ul>
        <li>Peer discovery uses <strong>public nostr relays</strong>. They learn an ephemeral room id and that two browsers are connecting, but the handshake is encrypted with your passphrase — they can’t read it or your files.</li>
        <li>Your <strong>IP address</strong> is visible to the device you connect to and to the public ${g('stun', 'STUN')} server — inherent to any peer-to-peer session.</li>
        <li>NAT traversal uses public STUN only. A ${g('symmetric nat', 'symmetric-NAT')} pair with no relay may fail to connect. We won’t add a relay because it would be a backend that could see traffic.</li>
        <li><strong>Your sync peer sees the files you sync</strong> — that’s the point. Only pair with a device or person you trust, and only pick a folder you mean to share.</li>
      </ul></section>
      <section><h3 class="tm-trust">Trust surface</h3><ul>
        <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the TLS chain to it.</li>
        <li>The public signalling relays and STUN server (encrypted handshake only; they never carry your files).</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your files are never sent to it.</li>
        <li>Feedback you choose to send goes to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your files are never sent.</li>
      </ul></section>
    </div>`;
}

export function aboutHtml(): string {
  return `
    <p>Syncwell keeps a folder mirrored between two devices — your laptop and your phone, or you and one collaborator — by sending the files directly, device-to-device, encrypted end-to-end. No cloud account, no upload, no server carrying your files. It runs entirely in your browser.</p>
    <p>It only ever adds and updates files; it never deletes. It’s part of a small collection of privacy-first, zero-backend browser tools. Everything happens on your device.</p>
    <p class="about-links">
      <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      <a href="https://github.com/ben-gy/syncwell" target="_blank" rel="noopener">source on GitHub</a>
    </p>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
